import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, Store, Nfc, Check } from 'lucide-react';

/**
 * Cash-system boot animation. Purely visual; timing is controlled by the parent.
 * Sequence: the POS terminal powers on, a credit card swipes in and is scanned
 * (contactless waves + scan line), payment is approved, and a receipt prints
 * while the running total rings up.
 */
export const BootSplash = ({ durationMs = 3000 }) => {
  const { t } = useTranslation();
  const [total, setTotal] = useState(0);
  const target = 128.5;

  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / (durationMs - 500));
      const eased = 1 - Math.pow(1 - p, 3);
      setTotal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [durationMs]);

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center bg-background px-6 overflow-hidden"
      data-testid="boot-splash">
      <div className="relative h-64 w-64">
        {/* Floating store badge */}
        <div className="absolute left-1/2 top-0 -translate-x-1/2 rmp-anim-float z-30">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-card shadow-md-token rmp-anim-pop">
            <Store className="h-6 w-6 text-primary" />
          </div>
        </div>

        {/* Credit card being scanned */}
        <div className="absolute left-1/2 top-[78px] z-20 -translate-x-1/2">
          <div className="rmp-anim-card relative h-28 w-44 overflow-hidden rounded-2xl shadow-lg-token"
            style={{ background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.7) 100%)' }}>
            {/* chip */}
            <div className="absolute left-4 top-5 h-6 w-8 rounded-md bg-primary-foreground/80" />
            <div className="absolute left-4 top-7 h-px w-8 bg-primary/40" />
            {/* contactless */}
            <Nfc className="absolute right-3 top-4 h-5 w-5 text-primary-foreground/90 rotate-90" />
            {/* number dots */}
            <div className="absolute bottom-7 left-4 flex gap-2">
              {[0, 1, 2, 3].map((g) => (
                <div key={g} className="flex gap-0.5">
                  {[0, 1, 2, 3].map((d) => <span key={d} className="h-1 w-1 rounded-full bg-primary-foreground/70" />)}
                </div>
              ))}
            </div>
            <div className="absolute bottom-3 left-4 h-1.5 w-16 rounded-full bg-primary-foreground/50" />
            {/* scan line sweeping the card */}
            <div className="rmp-anim-scan absolute inset-x-0 top-0 h-8"
              style={{ background: 'linear-gradient(180deg, transparent, hsl(var(--primary-foreground) / 0.55), transparent)' }} />
          </div>

          {/* contactless waves + approval check */}
          <div className="absolute -right-6 top-1/2 -translate-y-1/2 flex items-center">
            <span className="rmp-anim-wave block h-6 w-6 rounded-full border-2 border-primary/60" />
            <span className="rmp-anim-wave-2 -ml-3 block h-9 w-9 rounded-full border-2 border-primary/40" />
          </div>
          <div className="rmp-anim-approve absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-pos-free text-white shadow-md-token">
            <Check className="h-4 w-4" strokeWidth={3} />
          </div>
        </div>

        {/* POS terminal */}
        <div className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2 rmp-anim-pop">
          <div className="flex h-32 w-40 flex-col items-center rounded-3xl bg-primary px-3 pt-3 shadow-lg-token">
            {/* screen with ticking total */}
            <div className="rmp-anim-screen flex h-10 w-full items-center justify-between rounded-lg bg-background/95 px-2.5">
              <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Total</span>
              <span className="font-display text-sm font-extrabold text-foreground tabular">{total.toFixed(2)} €</span>
            </div>
            {/* slot */}
            <div className="mt-2 h-1.5 w-24 rounded-full bg-primary-foreground/30" />
            {/* receipt printing out */}
            <div className="rmp-anim-receipt mt-2 w-28 rounded-md border border-border bg-card px-2.5 py-2">
              <div className="flex items-center justify-center gap-1 text-primary">
                <Receipt className="h-3 w-3" />
                <span className="text-[8px] font-bold uppercase tracking-wide">{t('common.appName')}</span>
              </div>
              <div className="mt-1.5 space-y-1">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="rmp-anim-line h-1 rounded-full bg-muted"
                    style={{ width: ['90%', '65%', '80%'][i], animationDelay: `${1500 + i * 160}ms` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Brand + progress */}
      <div className="rmp-anim-fade mt-6 text-center" style={{ animationDelay: '300ms' }}>
        <h1 className="font-display text-2xl font-black tracking-tight">{t('common.appName')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('access.bootTagline')}</p>
      </div>
      <div className="mt-5 h-1 w-48 overflow-hidden rounded-full bg-muted">
        <div className="rmp-anim-progress h-full w-full rounded-full bg-primary" />
      </div>
    </div>
  );
};
